import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { Box, lighten, Tooltip, useTheme } from "@mui/material";
import { CommentCell } from "../../components/heat-results/cells";
import { ColumnDef, ProcessedRow } from "../models/utils";
import { billetStatus } from "../shared/billetStatus";
import { EventTypeTag } from "../shared/EventTypeTag";
import { eventTypes } from "../shared/eventTypes";
import {
	getEventMonthScheduledRatio,
	isMultiMonthEvent as _isMultiMonth,
} from "../utils/calculateFields";
import { resolveTargetTPH } from "../utils/targetTPH";

const formatTons = (value: number | null | undefined): string => {
	if (!value) return "";
	if (typeof value !== "number" || !isFinite(value)) return "";
	return value.toLocaleString("en-US", {
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	});
};

const formatPercent = (value: number | null | undefined): string => {
	if (!value) return "";
	if (typeof value !== "number" || !isFinite(value)) return "";
	return `${value.toFixed(2)}%`;
};

const getRowBackgroundColor = ({
	row,
	hoveredBilletStatusCode,
	useColors,
}: {
	row: ProcessedRow;
	hoveredBilletStatusCode?: string | null;
	useColors?: boolean;
}): string => {
	const isPlaceholder = row.EventId < 0;
	const isHoveredBillet =
		hoveredBilletStatusCode &&
		row.BilletsStatusCode === hoveredBilletStatusCode;

	if (row.IsMTDSummary) return "#FFEB3B";
	if (row.IsMonthSummary) return "#E3F2FD";
	if (isPlaceholder) return "#f5f5f5";

	if (useColors && row.SectionId) {
		const goldenRatio = 0.618033988749;
		const hue = (row.SectionId * goldenRatio * 360) % 360;
		const saturation = 40 + ((row.SectionId * 17) % 20);
		const lightness = 70 + ((row.SectionId * 13) % 15);
		return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
	}

	const billetstatus = billetStatus.find(
		(s) => s.code === row.BilletsStatusCode,
	);

	if (isHoveredBillet) return billetstatus?.hoverColor ?? "#fcfcfcff";

	return "#fcfcfcff";
};

// Use OriginalStart/OriginalEnd (actual event dates) — row.Start/End are day-sliced calendar boundaries.
const isMultiMonthEvent = (row: ProcessedRow): boolean =>
	_isMultiMonth(row.OriginalStart ?? row.Start, row.OriginalEnd ?? row.End);

const getMonthRatio = (row: ProcessedRow, calendarMonth: number): number => {
	const start = row.OriginalStart ?? row.Start;
	const end = row.OriginalEnd ?? row.End;
	if (!start || !end) return 1;
	return getEventMonthScheduledRatio(
		start,
		end,
		calendarMonth,
		row.ScheduledRollingHours,
	);
};

const getEffectiveTargetTPH = (row: ProcessedRow): number | null | undefined =>
	row.EffectiveTargetTPH ??
	resolveTargetTPH({
		TargetTPH: row.TargetTPH,
		SectionTargetTPH: row.SectionTargetTPH,
		TargetTPHPercentage: row.TargetTPHPercentage,
		SectionTargetTPHPercentage: row.SectionTargetTPHPercentage,
	});

// Two-line cell: top = scheduled/target (gray), bottom = actual (bold).
const StackedCell = ({
	top,
	bottom,
	topTooltip,
}: {
	top: string;
	bottom: string;
	topTooltip?: string;
}) => {
	if (!top && !bottom) return null;
	const topLine = (
		<Box sx={{ color: "#666", fontWeight: "normal", fontSize: "0.95em" }}>
			{top || "\u00A0"}
		</Box>
	);
	return (
		<Box sx={{ lineHeight: 1.35 }}>
			{topTooltip ? (
				<Tooltip title={topTooltip} arrow placement="top">
					{topLine}
				</Tooltip>
			) : (
				topLine
			)}
			<Box sx={{ fontWeight: "bold" }}>{bottom || "\u00A0"}</Box>
		</Box>
	);
};

type ScheduledTonsField =
	| "ScheduledChargedTons"
	| "ScheduledRolledTons"
	| "ScheduledFinishedTons";
type ActualTonsField =
	| "ActualChargedTons"
	| "ActualRolledTons"
	| "ActualFinishedTons";

// Multi-month events show the month-prorated value; the event total moves into a tooltip.
const getScheduledTons = (
	row: ProcessedRow,
	field: ScheduledTonsField,
	calendarMonth: number,
): { value: string; tooltip?: string } => {
	const total = formatTons(row[field]);
	if (!isMultiMonthEvent(row)) return { value: total };
	const ratio = getMonthRatio(row, calendarMonth);
	const monthValue = formatTons(Math.round((row[field] ?? 0) * ratio));
	return {
		value: monthValue,
		tooltip: total ? `Event total: ${total}` : undefined,
	};
};

interface useCustomTableColumnsProps {
	calendarMonth?: number;
}

export const useCustomTableColumns = ({
	calendarMonth = new Date().getMonth(),
}: useCustomTableColumnsProps) => {
	const theme = useTheme();

	const makeTonsColumn = (
		headerName: string,
		scheduledField: ScheduledTonsField,
		actualField: ActualTonsField,
	): ColumnDef => ({
		field: actualField,
		headerName,
		headerSub: { top: "Scheduled", bottom: "Actual" },
		flex: 1,
		align: "center",
		spanRows: true,
		render: (row) => {
			const scheduled = getScheduledTons(row, scheduledField, calendarMonth);
			return (
				<StackedCell
					top={scheduled.value}
					bottom={formatTons(row[actualField])}
					topTooltip={scheduled.tooltip}
				/>
			);
		},
	});

	const columns: ColumnDef[] = [
		{
			field: "EventTypeCode",
			headerName: "Type",
			flex: 1,
			hide: true,
			render: (row) => {
				const typeCode = row.EventTypeCode;
				if (!typeCode) return null;
				const eventType = eventTypes.find((t) => t.code === typeCode);
				if (!eventType) return typeCode;
				return (
					<EventTypeTag
						event={{ Code: eventType.code, Name: eventType.name }}
						show="name"
					/>
				);
			},
		},
		{
			field: "Start",
			headerName: "Date",
			flex: 0.6,
			align: "center",
			spanRowsByDay: true,
			getValue: (row) => dxtToLocalServerTime(row.Start, "dd") || "",
			render: (row) => {
				if (row.IsMTDSummary || row.IsMonthSummary) return "";
				return dxtToLocalServerTime(row.Start, "dd") || "";
			},
			cellStyle: () => ({ verticalAlign: "middle" }),
		},
		{
			field: "SectionName",
			headerName: "Section",
			flex: 1.2,
			align: "center",
			spanRows: true,
			getValue: (row) => row.SectionName ?? row.EventTypeName,
			render: (row, _value) => {
				// Summary rows lost their Description column: show the label here.
				if (row.IsMTDSummary || row.IsMonthSummary) {
					return (
						<Box
							sx={{
								width: "100%",
								textAlign: "center",
								fontWeight: "bold",
								whiteSpace: "normal",
								lineHeight: 1.2,
							}}
						>
							{row.Description}
						</Box>
					);
				}
				const displayValue = row.SectionName ?? row.EventTypeName;
				const content = (
					<Box
						sx={{
							width: "100%",
							height: "100%",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							position: "relative",
						}}
					>
						{displayValue}
					</Box>
				);
				if (row.Description) {
					return (
						<Tooltip title={row.Description} arrow placement="top">
							{content}
						</Tooltip>
					);
				}
				return content;
			},
			cellStyle: (row) => {
				if (row.EventId < 0 || row.IsMTDSummary || row.IsMonthSummary)
					return {};

				const isProductionEvent = row.EventTypeCode === "P";
				const eventType = eventTypes.find((e) => e.code === row.EventTypeCode);
				const billetstatus = billetStatus.find(
					(s) => s.code === row.BilletsStatusCode,
				);

				const backgroundColor = isProductionEvent
					? (billetstatus?.backgroundColor ?? "transparent")
					: (eventType?.backgroundColor ?? "transparent");

				return { backgroundColor };
			},
		},
		makeTonsColumn("Charged", "ScheduledChargedTons", "ActualChargedTons"),
		makeTonsColumn("Rolled", "ScheduledRolledTons", "ActualRolledTons"),
		makeTonsColumn("Finished", "ScheduledFinishedTons", "ActualFinishedTons"),
		{
			field: "ActualTPH",
			headerName: "TPH",
			headerTooltip: "Target / Actual",
			headerSub: { top: "Targeted", bottom: "Actual" },
			flex: 1,
			align: "center",
			spanRows: true,
			render: (row) => {
				const target = getEffectiveTargetTPH(row);
				return (
					<StackedCell
						top={target != null ? String(target) : ""}
						bottom={row.ActualTPH != null ? String(row.ActualTPH) : ""}
					/>
				);
			},
		},
		{
			field: "MillYield",
			headerName: "Yield",
			headerTooltip: "Mill / Finished",
			headerSub: { top: "Mill", bottom: "Finished" },
			flex: 1,
			align: "center",
			spanRows: true,
			render: (row) => {
				const millYield =
					row.StatusCode === "P" ? row.ScheduledMillYield : row.ActualMillYield;
				const finishedYield =
					row.StatusCode === "P"
						? row.ScheduledFinishedYield
						: row.ActualFinishedYield;
				return (
					<StackedCell
						top={formatPercent(millYield)}
						bottom={formatPercent(finishedYield)}
					/>
				);
			},
		},
		{
			field: "EventComments",
			headerName: "",
			width: 34,
			align: "center",
			spanRows: true,
			render: (row) => {
				if (row.EventId < 0) return null;
				return (
					<CommentCell
						comment={row.EventComments ?? null}
						label={
							row.SectionName ?? dxtToLocalServerTime(row.Start, "MM/dd/yyyy")
						}
					/>
				);
			},
			cellStyle: (row) => {
				const eventComments = row.EventComments ?? "";
				if (eventComments.toLowerCase().includes("down")) {
					return {
						backgroundColor: lighten(theme.palette.warning.main, 0.3),
					};
				}
				return {};
			},
		},
	];

	const visibleColumns = columns.filter((col) => !col.hide);

	return {
		columns,
		visibleColumns,
		getRowBackgroundColor,
	};
};
